import { useState } from 'react';
import { Check, Edit3, X } from 'lucide-react';
import { saveChatMessage } from '../lib/api';
import { useAuth } from '@clerk/clerk-react';
import { useToast } from '../hooks/useToast';

interface ActionNudgeProps {
  message: {
    id: string;
    text: string;
    coachId?: string;
  };
  sessionId: string;
  onDismiss: () => void;
  onSave: () => void;
}

const ActionNudge = ({ message, sessionId, onDismiss, onSave }: ActionNudgeProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(message.text);
  const [isSaving, setIsSaving] = useState(false);
  const { getToken } = useAuth();
  const { success, error } = useToast();

  const handleSave = async (text: string) => {
    try {
      setIsSaving(true);
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');

      await saveChatMessage(
        sessionId,
        message.id,
        text,
        message.coachId || 'samuel',
        token
      );

      success('Action saved to ledger!');
      onSave();
    } catch (err) {
      error('Failed to save action');
      console.error('Error saving action:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleQuickSave = () => {
    handleSave(message.text);
  };

  const handleEditSave = () => {
    if (editedText.trim()) {
      handleSave(editedText.trim());
    }
  };

  if (isEditing) {
    return (
      <div className="mt-3 rounded-2xl border border-royal-blue/20 bg-royal-blue/5 p-4">
        <div className="space-y-3">
          <p className="text-sm font-medium text-slate-700">Edit action before saving:</p>
          <textarea
            value={editedText}
            onChange={(e) => setEditedText(e.target.value)}
            className="w-full rounded-xl border border-slate-200 p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-royal-blue focus:border-transparent"
            rows={3}
            placeholder="Describe the action..."
          />
          <div className="flex gap-2">
            <button
              onClick={handleEditSave}
              disabled={isSaving || !editedText.trim()}
              className="inline-flex items-center gap-2 rounded-xl bg-royal-blue px-4 py-2 text-sm font-medium text-white hover:bg-royal-blue/90 disabled:opacity-50 transition-colors"
            >
              {isSaving ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              Save Action
            </button>
            <button
              onClick={() => setIsEditing(false)}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-2xl border border-royal-blue/20 bg-royal-blue/5 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <p className="text-sm font-medium text-royal-blue mb-1">Turn this into an action?</p>
          <p className="text-xs text-slate-600">Save this recommendation to your Action Ledger</p>
        </div>
        <button
          onClick={onDismiss}
          className="flex-shrink-0 p-1 text-slate-400 hover:text-slate-600 transition-colors"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="flex gap-2 mt-3">
        <button
          onClick={handleQuickSave}
          disabled={isSaving}
          className="inline-flex items-center gap-2 rounded-xl bg-royal-blue px-4 py-2 text-sm font-medium text-white hover:bg-royal-blue/90 disabled:opacity-50 transition-colors"
        >
          {isSaving ? (
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
          ) : (
            <Check className="h-4 w-4" />
          )}
          Save as Action
        </button>
        <button
          onClick={() => setIsEditing(true)}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
        >
          <Edit3 className="h-4 w-4" />
          Edit & Save
        </button>
        <button
          onClick={onDismiss}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
        >
          Not now
        </button>
      </div>
    </div>
  );
};

export default ActionNudge;
